import 'reflect-metadata';
import { StringHelpers } from "../helpers/StringHelpers";
import { IEntity } from "./IEntity";
import { IEntityMetaData, IMetaProprety, IMetaPropretyArgs, IUxMessage, IPropretyAttribute } from "./IEntityMetaData";
import { RegexType, PropertyTypeCombination, RegexLibrary } from "../services/RegexPatternLibrary";
import { EntityClassRegistry } from "../decorators/DependencyInjectionRegistreyDecorators";
import { EntityState, IEntityState } from './IChangedEntity';

export enum KeyType {
  number = 'number',
  bigInt = 'bigInt',
  string = 'string',
  any = 'any'
}

export abstract class GenericEntityBase<TKey> implements IEntity, IEntityState {
  [key: string]: any;
  abstract id: TKey;
  abstract GetType(): IEntityMetaData;

  public _isRelationEntity = false;

  public GetMessages(): IUxMessage {
    return {
      Create: { ConfirmationRequest: ``, Fail: ``, Success: `` },
      Delete: { ConfirmationRequest: ``, Fail: ``, Success: `` },
      Update: { ConfirmationRequest: ``, Fail: ``, Success: `` },
    };
  }

  public static GetMessageFor(action: 'Create' | 'Update' | 'Delete', instance: IEntity, culture: 'default', langRessource: JSON) {
    // Implémentation a venir
  }

  public _entityState: EntityState = EntityState.Unchanged;
  public _modifiedProperties: Set<string> = new Set();
  public _modifiedBy?: string;
  public _modifiedAt?: Date;
  public _changeMap?: Map<string, { old: any; new: any }>;
  public _undoState?: Map<string, boolean>;

  // SEUL AJOUT : Cache statique
  private static metadataCache = new Map<Function, IEntityMetaData>();

  public static get EntityFullNames(): string[] {
    return Array.from(EntityClassRegistry.keys());
  }

  protected static GetDefaultId(keyType: KeyType): any {
    if (keyType === KeyType.string) {
      return "00000000-0000-0000-0000-000000000000";
    } else if (keyType === KeyType.number || keyType == KeyType.bigInt) {
      return 0;
    }
    return {};
  }

  private static generateValidationErrorMessage(descriptor: IMetaProprety): string {
    let baseMessage = '';
    let fallBackValidationMsg = 'messages.field-validation-errors.fallback-validation';

    if (descriptor.ValidationErrorMessage && descriptor.ValidationErrorMessage.trim().length > 0) {
      baseMessage = descriptor.ValidationErrorMessage.trim();
    }
    else if (descriptor.PropretyType) {
      baseMessage = RegexLibrary.getValidationErrorMessage(descriptor.PropretyType) || fallBackValidationMsg;
    } else if (descriptor.ValidationPattern) {
      baseMessage = fallBackValidationMsg;
    } else {
      baseMessage = 'messages.field-validation-errors.invalid-field';
    }

    const hasMinLength = descriptor.MinLength !== undefined && descriptor.MinLength !== null;
    const hasMaxLength = descriptor.MaxLength !== undefined && descriptor.MaxLength !== null;

    let intervalPart = '';
    if (hasMinLength && hasMaxLength) {
      intervalPart = `, interval:[${descriptor.MinLength},  ${descriptor.MaxLength} ]`;
    } else if (hasMinLength) {
      intervalPart = `, interval:[${descriptor.MinLength}, ]`;
    } else if (hasMaxLength) {
      intervalPart = `, interval:[ , ${descriptor.MaxLength} ]`;
    }

    let propsPart = `propreties:[${descriptor.PropretyKey}]`
    return `##{${propsPart}${intervalPart}}##__${baseMessage}`;
  }

  private static generateRegexFromType(propertyType: PropertyTypeCombination, minLength?: number, maxLength?: number): string | undefined {
    let basePattern: string | undefined;
    const extractCore = (regex: string): string => regex.replace(/^\^/, '').replace(/\$$/, '');
    if (propertyType.includes('|')) {
      const types = propertyType.split('|') as RegexType[];
      const patterns: string[] = [];
      for (const type of types) {
        const pattern = this.getPatternForType(type);
        if (pattern && pattern !== '^[\\s\\S]*$') {
          patterns.push(extractCore(pattern));
        }
      }
      if (patterns.length > 0) {
        basePattern = `(?:${patterns.join('|')})`;
      }
    } else {
      const pattern = this.getPatternForType(propertyType as RegexType);
      if (pattern) {
        basePattern = extractCore(pattern);
      }
    }
    if (basePattern) {
      const regex = `^${basePattern}$`;
      return regex;
    }
    return undefined;
  }

  private static getPatternForType(type: RegexType, customMinLength?: number, customMaxLength?: number): string | undefined {
    const validation = RegexLibrary.validations.get(type);
    if (!validation) {
      return undefined;
    }

    let pattern = validation.pattern;
    let min = customMinLength ?? validation.minLength;
    let max = customMaxLength ?? validation.maxLength;
    let forceNegativeSupport = type === RegexType.Numeric || type === RegexType.Decimal;
    let source = pattern;

    if (forceNegativeSupport) {
      if (!source.includes('^-?')) {
        source = source.replace(/^\^/, '^-?');
        if (!source.startsWith('^')) {
          source = '^' + source;
        }
      }
    }

    const hasLengthQuantifier = /[{}*+?]$/.test(source) || source.includes('{') && false;

    if (!hasLengthQuantifier) {
      let content = source;
      if (source.startsWith('^') && source.endsWith('$')) {
        content = source.slice(1, -1);
      }

      if (min > 0 || max > 0) {
        if (max === min) {
          source = `^(?=.{${min}}$)___${content}$`;
        } else if (max === Infinity || max === 0) {
          source = `^(?=.{${min},}$)___${content}$`;
        } else {
          source = `^(?=.{${min},${max}}$)___${content}$`;
        }

        source = source.replace('}$)___', '}$)').replace(')___', ')');
      } else {
        source = `^${content}$`;
      }
    }
    return source;
  }

  private static inferTypeFromDesignType(designType: any): RegexType | undefined {
    if (designType === String) return undefined;
    if (designType === Number) return RegexType.Decimal;
    if (designType === Boolean) return undefined;
    return undefined;
  }

  private static getDefaultLengthsForType(propertyType?: PropertyTypeCombination): { min?: number, max?: number } {
    if (!propertyType) return {};
    if (propertyType.includes('|')) {
      const types = propertyType.split('|') as RegexType[];
      let result: { min?: number, max?: number } = {};
      for (const type of types) {
        const lengths = RegexLibrary.getDefaultLengths(type);
        if (lengths) {
          result.min = result.min !== undefined ? Math.max(result.min, lengths.min ?? 0) : lengths.min;
          result.max = result.max !== undefined ? Math.min(result.max, lengths.max ?? Infinity) : lengths.max;
        }
      }
      return result;
    } else {
      const lengths = RegexLibrary.getDefaultLengths(propertyType as RegexType);
      return lengths || {};
    }
  }

  public static getMetaData<T extends IEntity>(this: new () => T, parentTypes: string[] = [], depth: number = 0): IEntityMetaData {
    // SEUL AJOUT : Vérification du cache
    if (parentTypes.length === 0 && GenericEntityBase.metadataCache.has(this)) {
      return GenericEntityBase.metadataCache.get(this)!;
    }

    const instance = new this();
    const meta = Reflect.getMetadata('entityMetaData', instance) || {};
    let entityName = meta.EntityName || this.name;
    entityName = entityName.startsWith('_') ? entityName.substring(1) : entityName;

    if (parentTypes.includes(entityName) || depth > 4) {
      return {
        EntityName: entityName,
        EntityPluralName: meta.EntityPluralName || StringHelpers.Pluralize(entityName),
        EntityDisplayName: StringHelpers.Humanize(entityName),
        EntityDisplayPlural: StringHelpers.Humanize(meta.EntityPluralName || StringHelpers.Pluralize(entityName)),
        EndPoint: meta.EndPoint || `/${(meta.EntityPluralName || StringHelpers.Pluralize(entityName)).toLowerCase()}`,
        PropertyNames: [],
        PropretyDescriptors: {},
        Namespace: Reflect.getMetadata('custom:entity_namespace', this),
        UseTokenCheck: meta.UseTokenCheck ?? true,
        EntityKind: instance._isRelationEntity ? 'Association' : 'Business'
      };
    }

    const properties = Object.getOwnPropertyNames(instance).filter(prop => prop !== 'constructor');
    const entityPluralName = meta.EntityPluralName || StringHelpers.Pluralize(entityName);
    const propDescriptors = GenericEntityBase.GetEntityMetadata(instance, [...parentTypes, entityName], depth + 1);
    const completeDescriptors: Record<string, IMetaProprety> = {};

    for (const prop of properties) {
      if (propDescriptors[prop]) {
        completeDescriptors[prop] = propDescriptors[prop];
      } else {
        completeDescriptors[prop] = {
          PropretyKey: `entities.${`${entityName[0].toLowerCase()}${entityName.slice(1)}`}.propreties.${prop}`,
          PropretyName: prop,
          PropretyAttributes: [],
          PropretyDisplayName: StringHelpers.Humanize(prop),
          PropretyPluralName: StringHelpers.Pluralize(prop),
          PropretyDisplayPlural: StringHelpers.Humanize(StringHelpers.Pluralize(prop))
        };
      }
    }

    GenericEntityBase.generateAutoValidationPatterns(instance, completeDescriptors);
    GenericEntityBase.generateValidationErrorMessages(completeDescriptors);
    GenericEntityBase.generateRequiredErrorMessages(completeDescriptors);

    const result: IEntityMetaData = {
      EntityName: entityName,
      EntityPluralName: entityPluralName,
      EntityDisplayName: StringHelpers.Humanize(entityName),
      EntityDisplayPlural: StringHelpers.Humanize(entityPluralName),
      EndPoint: meta.EndPoint || `/${entityPluralName.toLowerCase()}`,
      PropertyNames: properties,
      PropretyDescriptors: completeDescriptors,
      Namespace: Reflect.getMetadata('custom:entity_namespace', this),
      UseTokenCheck: meta.UseTokenCheck ?? true,
      EntityKind: instance._isRelationEntity ? 'Association' : 'Business'
    };

    // SEUL AJOUT : Stockage dans le cache
    if (parentTypes.length === 0) {
      GenericEntityBase.metadataCache.set(this, result);
    }

    return result;
  }


  private static generateRequiredErrorMessages(descriptors: Record<string, IMetaProprety>): void {
    for (const descriptor of Object.values(descriptors)) {
      if (descriptor.PropretyAttributes.includes(IPropretyAttribute.Required) &&
        !descriptor.RequiredErrorMessage) {
        descriptor.RequiredErrorMessage = this.generateRequiredErrorMessage(descriptor);
      }
    }
  }

  private static generateRequiredErrorMessage(descriptor: IMetaProprety): string {
    return `##{propreties:[${descriptor.PropretyKey}]##__messages.field-validation-errors.missing-required`;
  }

  private static generateValidationErrorMessages(descriptors: Record<string, IMetaProprety>): void {
    for (const descriptor of Object.values(descriptors)) {
      if (descriptor.ValidationPattern || descriptor.PropretyType ||
        descriptor.MinLength !== undefined || descriptor.MaxLength !== undefined) {
        descriptor.ValidationErrorMessage = this.generateValidationErrorMessage(descriptor);
      }
    }
  }

  private static generateAutoValidationPatterns(instance: any, descriptors: Record<string, IMetaProprety>): void {
    for (const [propName, descriptor] of Object.entries(descriptors)) {
      const isObjectProp = descriptor.PropretyAttributes.includes(IPropretyAttribute.NavigationProperty) ||
        descriptor.PropretyAttributes.includes(IPropretyAttribute.NestedProperty);
      if (isObjectProp || descriptor.ValidationPattern) {
        continue;
      }
      const defaultLengths = this.getDefaultLengthsForType(descriptor.PropretyType);
      if (descriptor.MinLength === undefined && defaultLengths.min !== undefined) {
        descriptor.MinLength = defaultLengths.min;
      }
      if (descriptor.MaxLength === undefined && defaultLengths.max !== undefined) {
        descriptor.MaxLength = defaultLengths.max;
      }
      if (descriptor.PropretyType) {
        descriptor.ValidationPattern = this.generateRegexFromType(descriptor.PropretyType, descriptor.MinLength, descriptor.MaxLength);
        continue;
      }
      try {
        const designType = Reflect.getMetadata('design:type', instance, propName);
        const inferredType = this.inferTypeFromDesignType(designType);
        if (inferredType) {
          const inferredDefaultLengths = this.getDefaultLengthsForType(inferredType);
          if (descriptor.MinLength === undefined && inferredDefaultLengths.min !== undefined) {
            descriptor.MinLength = inferredDefaultLengths.min;
          }
          if (descriptor.MaxLength === undefined && inferredDefaultLengths.max !== undefined) {
            descriptor.MaxLength = inferredDefaultLengths.max;
          }
          descriptor.ValidationPattern = this.generateRegexFromType(
            inferredType,
            descriptor.MinLength,
            descriptor.MaxLength
          );
        } else if (designType === String) {
          descriptor.MinLength = descriptor.MinLength ?? 0;
          descriptor.MaxLength = descriptor.MaxLength ?? 4000;
          descriptor.ValidationPattern = `^[\\s\\S]{${descriptor.MinLength},${descriptor.MaxLength}}$`;
        } else {
          descriptor.ValidationPattern = undefined;
        }
      } catch (error) {
        descriptor.ValidationPattern = undefined;
      }
    }
  }

  public static GetEntityMetadata(instance: IEntity, parentTypes: string[] = [], depth: number = 0): Record<string, IMetaProprety> {
    let finalEntityName = parentTypes[parentTypes.length - 1];
    const proto = Object.getPrototypeOf(instance);
    const metaKeys = Object.getOwnPropertyNames(proto).filter(key => key.startsWith('__metaData_'));
    const descriptors: Record<string, IMetaProprety> = {};

    for (const metaKey of metaKeys) {
      const propertyKey = metaKey.replace('__metaData_', '');
      const decoratorMap = proto[metaKey] as Map<string, IMetaPropretyArgs>;
      if (!decoratorMap) continue;

      if (!descriptors[propertyKey]) {
        descriptors[propertyKey] = {
          PropretyKey: `entities.${`${finalEntityName[0].toLowerCase()}${finalEntityName.slice(1)}`}.propreties.${propertyKey}`,
          PropretyName: propertyKey,
          PropretyAttributes: [],
          PropretyDisplayName: StringHelpers.Humanize(propertyKey),
          PropretyPluralName: StringHelpers.Pluralize(propertyKey),
          PropretyDisplayPlural: StringHelpers.Humanize(StringHelpers.Pluralize(propertyKey))
        };
      }

      const descriptor = descriptors[propertyKey];
      for (const [decoratorName, metaArgs] of decoratorMap) {
        this.mergeMetadata(descriptor, metaArgs, decoratorName, parentTypes, depth);
      }
    }
    return descriptors;
  }

  private static mergeMetadata(descriptor: IMetaProprety, metaArgs: IMetaPropretyArgs, decoratorName: string, parentTypes: string[] = [], depth: number = 0): void {
    if (decoratorName === 'PropretyAttributes') {
      if (metaArgs.attributes) {
        for (const attribute of metaArgs.attributes) {
          if (!descriptor.PropretyAttributes.includes(attribute)) {
            descriptor.PropretyAttributes.push(attribute);
          }
        }
      }
      if (metaArgs.PropretyDisplayName !== undefined) {
        descriptor.PropretyDisplayName = metaArgs.PropretyDisplayName ?? '** No value Here **';
      }
      if (metaArgs.PropretyPluralName !== undefined) {
        descriptor.PropretyPluralName = metaArgs.PropretyPluralName ?? '** No value Here **';
        descriptor.PropretyDisplayPlural = StringHelpers.Humanize(metaArgs.PropretyPluralName ?? '** No value Here **');
      }
      if (metaArgs.ShowInTab !== undefined) descriptor.ShowInTab = metaArgs.ShowInTab;
      if (metaArgs.ShowAsNested !== undefined) descriptor.ShowAsNested = metaArgs.ShowAsNested;
      if (metaArgs.AddInplaceProprety !== undefined) descriptor.AddInplaceProprety = metaArgs.AddInplaceProprety;
      if (metaArgs.requiredErrorMessage) {
        descriptor.RequiredErrorMessage = metaArgs.requiredErrorMessage;
      }
    } else if (decoratorName === 'Validation') {
      if (metaArgs.pattern) descriptor.ValidationPattern = metaArgs.pattern;
      if (metaArgs.minLength !== undefined) descriptor.MinLength = metaArgs.minLength;
      if (metaArgs.maxLength !== undefined) descriptor.MaxLength = metaArgs.maxLength;
      if (metaArgs.propertyType) descriptor.PropretyType = metaArgs.propertyType;
      if (metaArgs.validationErrorMessage) descriptor.ValidationErrorMessage = metaArgs.validationErrorMessage;
      if (metaArgs.crossValidationFieldName) descriptor.CrossValidationFieldName = metaArgs.crossValidationFieldName;
    } else if (decoratorName === 'NavigationProperty') {
      if (!descriptor.PropretyAttributes.includes(IPropretyAttribute.NavigationProperty)) {
        descriptor.PropretyAttributes.push(IPropretyAttribute.NavigationProperty);
      }
      if (metaArgs.PropretyDisplayName !== undefined) {
        descriptor.PropretyDisplayName = metaArgs.PropretyDisplayName ?? '** No value Here **';
      }
      if (metaArgs.PropretyPluralName !== undefined) {
        descriptor.PropretyPluralName = metaArgs.PropretyPluralName ?? '** No value Here **';
        descriptor.PropretyDisplayPlural = StringHelpers.Humanize(metaArgs.PropretyPluralName ?? '** No value Here **');
      }
      if (metaArgs.ShowInTab !== undefined) descriptor.ShowInTab = metaArgs.ShowInTab;
      if (metaArgs.ShowAsNested !== undefined) descriptor.ShowAsNested = metaArgs.ShowAsNested;
      if (metaArgs.AddInplaceProprety !== undefined) descriptor.AddInplaceProprety = metaArgs.AddInplaceProprety;

      if (metaArgs.TargetType && (metaArgs.TargetType.EntityName  || metaArgs.TargetType.name )) {
        descriptor.TargetTypeName = metaArgs.TargetType.EntityName || metaArgs.TargetType.name;
       // descriptor.TargetTypeMetaData = metaArgs.TargetType.getMetaData(parentTypes, depth);
      }


    } else if (decoratorName === 'NestedProperty') {
      if (!descriptor.PropretyAttributes.includes(IPropretyAttribute.NestedProperty)) {
        descriptor.PropretyAttributes.push(IPropretyAttribute.NestedProperty);
      }
      if (metaArgs.PropretyDisplayName !== undefined) {
        descriptor.PropretyDisplayName = metaArgs.PropretyDisplayName ?? '** No value Here **';
      }
      if (metaArgs.PropretyPluralName !== undefined) {
        descriptor.PropretyPluralName = metaArgs.PropretyPluralName ?? '** No value Here **';
        descriptor.PropretyDisplayPlural = StringHelpers.Humanize(metaArgs.PropretyPluralName ?? '** No value Here **');
      }
      if (metaArgs.ShowInTab !== undefined) descriptor.ShowInTab = metaArgs.ShowInTab;
      if (metaArgs.ShowAsNested !== undefined) descriptor.ShowAsNested = metaArgs.ShowAsNested;
      if (metaArgs.AddInplaceProprety !== undefined) descriptor.AddInplaceProprety = metaArgs.AddInplaceProprety;
      if (metaArgs.TargetType) {
        descriptor.TargetTypeMetaData = metaArgs.TargetType.getMetaData(parentTypes, depth);
        descriptor.TargetTypeName = metaArgs.TargetType.name;
      }
    }
  }

  public static GetPropretiesByDecoratorFromMeta(metaData: IEntityMetaData, discriminant: IPropretyAttribute): IMetaProprety[] {
    return Object.values(metaData.PropretyDescriptors)
      .filter(desc => desc.PropretyAttributes.includes(discriminant));
  }

  public static CheckPropretyHasDecoratorFromMeta(metaData: IEntityMetaData, propretyName: string, discriminant: IPropretyAttribute): boolean {
    return metaData.PropretyDescriptors[propretyName]?.PropretyAttributes.includes(discriminant) ?? false;
  }

  public static clearMetadataCache(): void {
    GenericEntityBase.metadataCache.clear();
  }
}
